import { zodResolver } from '@hookform/resolvers/zod'
import { DocumentDownload } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LocationAutocomplete } from '@/components/LocationAutocomplete/LocationAutocomplete'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import type { submitSubjectInfoEditSchemaType } from '@/utils/schemas'
import { submitSubjectInfoEditSchema } from '@/utils/schemas'

import type { GooglePlace } from '../utils/applyGooglePlaceToForm'
import { extractLocationParts } from '../utils/applyGooglePlaceToForm'

export type SubjectInfoEditValue = submitSubjectInfoEditSchemaType & {
  countryShortName: string
  stateShortName: string
  cityShortName: string
}

interface SubjectInfoEditSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: SubjectInfoEditValue
  onSave: (next: SubjectInfoEditValue) => void
  consentFile?: string
  consentFileName?: string
  isEmailRequired?: boolean
}

export const SubjectInfoEditSheet = ({
  open,
  onOpenChange,
  value,
  onSave,
  consentFile,
  consentFileName,
  isEmailRequired = false,
}: SubjectInfoEditSheetProps) => {
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()

  const form = useForm<submitSubjectInfoEditSchemaType>({
    resolver: zodResolver(submitSubjectInfoEditSchema),
    defaultValues: value,
    mode: 'onChange',
  })

  const { handleSubmit, reset, setValue, control, formState } = form

  const [countryShortName, setCountryShortName] = useState(
    value.countryShortName,
  )
  const [stateShortName, setStateShortName] = useState(value.stateShortName)
  const [cityShortName, setCityShortName] = useState(value.cityShortName)

  useEffect(() => {
    if (open) {
      reset(value)
      setCountryShortName(value.countryShortName)
      setStateShortName(value.stateShortName)
      setCityShortName(value.cityShortName)
    }
  }, [open, value, reset])

  const handleCancel = () => onOpenChange(false)
  const handleSave = handleSubmit((data) => {
    onSave({
      ...data,
      countryShortName,
      stateShortName,
      cityShortName,
    })
    onOpenChange(false)
  })

  const handlePlaceSelected = (place: GooglePlace) => {
    const parts = extractLocationParts(place)
    setValue('countryLongName', parts.countryLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('stateLongName', parts.stateLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('cityLongName', parts.cityLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setCountryShortName(parts.countryShort)
    setStateShortName(parts.stateShort)
    setCityShortName(parts.cityShort)
  }

  const handleDownloadConsent = () => {
    if (!consentFile) return
    const link = document.createElement('a')
    link.href = consentFile
    link.download = consentFileName || 'consent.pdf'
    link.click()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className="flex flex-col gap-4 max-md:h-[85vh] max-md:rounded-t-2xl sm:max-w-md"
      >
        <SheetHeader className="text-center sm:text-center">
          <SheetTitle>
            {t('dashboard.runReport.submitReport.subjectInfoSheetTitle')}
          </SheetTitle>
        </SheetHeader>

        <FormProvider {...form}>
          <form
            onSubmit={handleSave}
            className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto"
          >
            <FormField
              control={control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.subjectInfoFields.firstName')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.subjectInfoFields.firstNamePlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="middleName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.subjectInfoFields.middleName')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.subjectInfoFields.middleNamePlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.subjectInfoFields.lastName')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.subjectInfoFields.lastNamePlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="alias"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.subjectInfoFields.alias')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.subjectInfoFields.aliasPlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="dob"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.subjectInfoFields.age')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.subjectInfoFields.agePlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.subjectInfoFields.email')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder={t(
                        'dashboard.runReport.subjectInfoFields.emailPlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.addExtraDetails.phoneNumber')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="tel"
                      placeholder={t(
                        'dashboard.runReport.addExtraDetails.phoneNumberPlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="employerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.addExtraDetails.employer')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.addExtraDetails.employerPlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="countryLongName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.subjectInfoFields.location')}
                  </FormLabel>
                  <FormControl>
                    <LocationAutocomplete
                      onPlaceSelected={handlePlaceSelected}
                      defaultValue={field.value}
                      placeholder={t(
                        'dashboard.runReport.subjectInfoFields.locationPlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="college"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.addExtraDetails.school')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.addExtraDetails.schoolPlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {consentFile && (
              <div className="flex flex-col gap-2 pt-2">
                <p className="text-sm text-neutralColor">
                  {t('dashboard.runReport.submitReport.downloadConsent')}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleDownloadConsent}
                  className="gap-2"
                >
                  <span className="truncate">
                    {consentFileName || 'consent.pdf'}
                  </span>
                  <DocumentDownload size="16" color="currentColor" />
                </Button>
              </div>
            )}
          </form>
        </FormProvider>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={handleCancel}>
            {t('dashboard.runReport.submitReport.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!formState.isValid}
          >
            {t('dashboard.runReport.submitReport.confirm')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
